import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Content } from '@/components/cardWithThreeFooterBox';
import { GridWidget } from '@/components/GridWidget.tsx';
import { fromApiDateTime, Icon } from '@/utils';

import {
  AggregatedChartConfigType,
  ChartAggregationResolution,
  ChartInterval,
  ChartProps,
  ChartType,
  ICardDataState,
  PlotType,
} from '../types.ts';
import { AggregatedHighChart } from './aggregatedChartCard/AggregatedHighChart.tsx';

/**
 * Set config or use localStorage via useChartStorage
 * @param id
 * @param onOpenModal
 * @param onRemove
 * @param config
 * @param hideEditChart
 * @constructor
 */
export const AggregatedChartCard = ({
  id,
  onOpenModal,
  config,
  hideEditChart,
  ...rest
}: ChartProps<AggregatedChartConfigType>) => {
  const { t } = useTranslation(['common', 'chartConfig']);

  const [chartDataStat, setChartDataStat] = useState<ICardDataState>({
    isLoading: true,
    isError: false,
    isEmpty: false,
  });

  const [chartName, setChartName] = useState<string | undefined>(undefined);

  const chartConfig = useMemo<AggregatedChartConfigType>(() => {
    const cfg = {
      ...defaultConfig,
      ...config,
    };

    // Ensure datetimes are local DateTimes (API strings may include an offset).
    if (typeof cfg.from === 'string') cfg.from = fromApiDateTime(cfg.from);
    if (typeof cfg.to === 'string') cfg.to = fromApiDateTime(cfg.to);

    return cfg;
  }, [config]);

  return (
    <GridWidget
      {...rest}
      id={id}
      headerTitle={chartConfig?.title || ''}
      headerSubtitle={chartName}
      {...chartDataStat}
      emptyMessages={[t('chartConfig:noReadings'), t('chartConfig:noReadingsMessage')]}
      toolbarActionList={
        hideEditChart
          ? undefined
          : [
              {
                id: uuidv4(),
                label: t('common:edit'),
                onClick: onOpenModal,
                Icon: Icon.SlidersHorizontal,
              },
            ]
      }
    >
      <Content>
        <AggregatedHighChart
          config={chartConfig}
          setChartDataStat={setChartDataStat}
          onChartNameChange={setChartName}
        />
      </Content>
    </GridWidget>
  );
};

// ************************************
// Constants
// ************************************
const defaultConfig: AggregatedChartConfigType = {
  plotType: PlotType.AGGREGATED,
  title: '',
  sensors: [],
  type: ChartType.LIVE,
  plot: [],
  interval: ChartInterval.Day,
  resolution: ChartAggregationResolution.HOURLY,
};
